import {useState, useEffect} from 'react'
import Button from '../../ui/Button/Button.tsx'
import IconClose from '../../../assets/icons/icon-close.svg?react'
import IconOptions from '../../../assets/icons/icon-options.svg?react'
import IconButton from '@/components/ui/IconButton/IconButton'
import DropdownWrapper from '@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper'
import Dropdown from '../../ui/Dropdown/Dropdown'
import DropdownItem from '@/components/ui/Dropdown/components/DropdownItem/DropdownItem'
import IconLogo from '../../../assets/icons/img-logo.svg?react'
import IconResetLink from '../../../assets/icons/img-reset-link.svg?react'
import Modal from '../Modal/Modal.tsx'
import css from './SignInModal.module.css'
import FormColumn from "@/components/ui/Form/components/FormColumn/FormColumn.tsx";
import Input from "../../ui/Input/Input.tsx";
import {signInSaga, signInGoogleSaga, resetPasswordSaga} from "../../../store/auth/authSlice.ts";
import {TextH2, TextNorm, TextSmall} from "../../ui/Text";
import {useAppSelector, useAppDispatch} from "../../../store/hooks.ts";
import {selectSignInError, selectIsLoading, selectOpenDropdownId, selectResetPasswordError} from "../../../store/ui/uiSelectors.ts";
import {setSignInError, removeLoadingKey, setOpenDropdown} from "../../../store/ui/uiSlice.ts";

const DROPDOWN_ID = 'sign-in-modal-options'

interface SignInModalProps {
    onClose: () => void
    // When false, clicking the backdrop / pressing Esc won't dismiss it (used on the
    // logged-out deep-link sign-in page so an accidental click doesn't lose the link).
    canClose?: boolean
    // Pre-fill the email field — used when arriving from an invite email link, so the
    // invitee doesn't have to retype the address the invite was sent to.
    initialEmail?: string
}

const SignInModal = ({onClose, canClose = true, initialEmail = ''}: SignInModalProps) => {
    const dispatch = useAppDispatch()
    const signInError = useAppSelector(selectSignInError)
    const isGoogleLoading = useAppSelector(selectIsLoading('sign-in-google'))
    const isSignInLoading = useAppSelector(selectIsLoading('sign-in'))
    const isResetLoading = useAppSelector(selectIsLoading('reset-password'))
    const resetPasswordError = useAppSelector(selectResetPasswordError)
    const openDropdownId = useAppSelector(selectOpenDropdownId)
    const isDropdownOpen = openDropdownId === DROPDOWN_ID
    useEffect(() => {
        const handler = (e: PageTransitionEvent) => {
            if (e.persisted) dispatch(removeLoadingKey('sign-in-google'))
        }
        window.addEventListener('pageshow', handler)
        return () => window.removeEventListener('pageshow', handler)
    }, [dispatch])

    const [email, setEmail] = useState(initialEmail)
    const [password, setPassword] = useState('')

    // initialEmail can arrive after mount (resolved async from an invite token). Seed the
    // field once it shows up, without clobbering anything the user has already typed.
    useEffect(() => {
        if (initialEmail) setEmail(prev => prev || initialEmail)
    }, [initialEmail])
    const [view, setView] = useState<'signIn' | 'forgotPassword'>('signIn')
    const [resetSent, setResetSent] = useState(false)
    const [emailError, setEmailError] = useState(false)

    const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

    const clearErrors = () => {
        if (emailError) setEmailError(false)
        if (signInError) dispatch(setSignInError(null))
    }

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.currentTarget.value)
        clearErrors()
    }

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.currentTarget.value)
        clearErrors()
    }

    const handleSubmit = () => {
        if (!isValidEmail(email)) {
            setEmailError(true)
            return
        }
        if (email && password) {
            dispatch(signInSaga({email, password}))
        }
    }

    const handleGoogleSignIn = () => {
        dispatch(signInGoogleSaga())
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSubmit()
        }
    }

    useEffect(() => {
        if (!isResetLoading && resetSent && resetPasswordError) {
            setResetSent(false)
        }
    }, [isResetLoading])

    const handleResetSubmit = () => {
        if (!isValidEmail(email)) {
            setEmailError(true)
            return
        }
        setEmailError(false)
        dispatch(resetPasswordSaga(email))
        setResetSent(true)
    }

    const handleResetKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleResetSubmit()
        }
    }

    const handleForgotPassword = () => {
        dispatch(setOpenDropdown(null))
        setResetSent(false)
        setView('forgotPassword')
    }

    const handleOptionsToggle = () => {
        dispatch(setOpenDropdown(isDropdownOpen ? null : DROPDOWN_ID))
    }

    const handleDropdownClose = () => {
        dispatch(setOpenDropdown(null))
    }

    if (view === 'forgotPassword') {
        return (
            <Modal onClose={onClose} canClose={canClose} maxWidth={416}>
                <div className={css.buttons}>
                    <IconButton icon={<IconClose />} onClick={onClose} />
                </div>

                {resetSent ? (
                    <>
                        <IconResetLink/>
                        <TextH2>Check your inbox</TextH2>
                        <div className={css.content}>
                            <TextNorm>Reset link sent to {email}. Please check your inbox for further
                                instructions</TextNorm>
                        </div>
                        <div className={css.bottom_button}>
                            <FormColumn>
                                <Button title={"Back to sign in"} type="white" size="medium"
                                        onClick={() => setView('signIn')}/>
                            </FormColumn>
                        </div>
                    </>
                ) : (
                    <>
                        <TextH2>Forgot pass?</TextH2>
                        <div className={css.content}>
                            <TextNorm>No worries, enter your email address to receive a password reset link.</TextNorm>
                        </div>
                        <FormColumn>
                            <Input
                                placeholder={"Email"}
                                value={email}
                                onChange={handleEmailChange}
                                onKeyDown={handleResetKeyDown}
                            />
                            {emailError && (
                                <div className={css.error}>
                                    <TextSmall>Enter a valid email address</TextSmall>
                                </div>
                            )}
                        </FormColumn>
                        {resetPasswordError && (
                            <div className={css.error}>
                                <TextSmall>{resetPasswordError}</TextSmall>
                            </div>
                        )}
                        <div className={css.bottom_button}>
                            <FormColumn>
                                <Button title={"Send me the link"} type="black" size="medium"
                                        onClick={handleResetSubmit} loading={isResetLoading}/>
                            </FormColumn>
                        </div>
                    </>
                )}

            </Modal>
        )
    }

    return (
        <Modal onClose={onClose} canClose={canClose} maxWidth={416}>
            <div className={css.buttons}>
                <DropdownWrapper>
                    <IconButton icon={<IconOptions />} onClick={handleOptionsToggle} />
                    <Dropdown isOpen={isDropdownOpen} align={"right"} onClose={handleDropdownClose}>
                        <DropdownItem
                            title="Forgot password"
                            onClick={handleForgotPassword}
                        />
                    </Dropdown>
                </DropdownWrapper>
                <IconButton icon={<IconClose />} onClick={onClose} />
            </div>
            <div className={css.logo}>
                <IconLogo/>
            </div>
            <TextH2 className={css.title}>Log in or Sign up</TextH2>
            <TextNorm className={css.text}>Get your workspace and start organizing projects with your team.</TextNorm>

            <FormColumn gap={16}>
                <Input
                    placeholder={"Enter your email"}
                    value={email}
                    onChange={handleEmailChange}
                    onKeyDown={handleKeyDown}
                    error={emailError}
                    testId="sign-in-email"
                />
                {emailError && (
                    <div className={css.error}>
                        <TextSmall>Please enter a valid email address</TextSmall>
                    </div>
                )}
                <Input
                    placeholder={"Password"}
                    type="password"
                    value={password}
                    onChange={handlePasswordChange}
                    onKeyDown={handleKeyDown}
                    error={!!signInError}
                    testId="sign-in-password"
                />
                {signInError && (
                    <div className={css.error}>
                        <TextSmall>{signInError}</TextSmall>
                    </div>
                )}
            </FormColumn>
            <div className={css.submit}>
                <FormColumn>
                    <Button title={"Continue"} type="black" size="medium" onClick={handleSubmit} loading={isSignInLoading} testId="sign-in-submit"/>
                </FormColumn>
            </div>

            <div className={css.separator}>
                <div className={css.separator_line}></div>
                <div className={css.separator_text}><TextSmall>or</TextSmall></div>
                <div className={css.separator_line}></div>
            </div>
            <FormColumn>
                <Button title={"Continue with Google"} type="white" size="medium" onClick={handleGoogleSignIn} loading={isGoogleLoading}/>
            </FormColumn>

            {/*<div className={css.forgotPassword}>*/}
            {/*    <span className={css.link} onClick={handleForgotPassword}><TextSmall>Forgot password?</TextSmall></span>*/}
            {/*</div>*/}
            <div className={css.description}>
                <TextSmall>By continuing, you agree to the Terms & <br/> Conditions and Privacy Policy</TextSmall>
            </div>
        </Modal>
    )
}

export default SignInModal
